import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { PrintDataEntitas } from "@/domain/entities/Booking";
import dateToDateString from "@/app/infrastructures/misc/common-library/DateToDateString";
import ellipsisString from "@/app/infrastructures/misc/common-library/EllipsisString";
import startCase from "lodash/startCase";
import { createI18n } from "vue-i18n";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";
import { formatByCountry } from "@/app/infrastructures/misc/Utils";

function setupI18n(language: string) {
  const i18nInstance = createI18n({
    legacy: false,
    locale: language,
    fallbackLocale: "id",
    globalInjection: false,
    messages: { en, id, my }
  });
  return i18nInstance.global.t;
}

function drawPartyInfo(doc: any, headerData: PrintDataEntitas, t: Function) {
  const address = `${ellipsisString(
    headerData.sttRecipientAddress.toUpperCase(),
    96
  )}.\n${headerData.sttDestinationDistrictName}, ${headerData.sttPostalCode}`;

  const parties = [
    {
      label: t("invoice.shipperName"),
      value: ellipsisString(headerData.sttSenderName, 17).toUpperCase(),
      x: 7,
      y: 53
    },
    {
      label: t("invoice.shipperPhone"),
      value: headerData.sttSenderPhone,
      x: 7,
      y: 60
    },
    {
      label: t("invoice.shipperAddress"),
      value: headerData.sttOriginCityName,
      x: 7,
      y: 67
    },
    {
      label: t("invoice.dutyTaxTerms"),
      value: headerData.sttDutyTaxTerms,
      x: 7,
      y: 74
    },
    {
      label: t("invoice.conssigneeName"),
      value: ellipsisString(headerData.sttRecipientName, 25).toUpperCase(),
      x: 90,
      y: 53
    },
    {
      label: t("invoice.conssigneePhone"),
      value: headerData.sttRecipientPhone,
      x: 90,
      y: 60
    },
    {
      label: t("invoice.conssigneeAddress"),
      value: address,
      x: 90,
      y: 67
    }
  ];

  parties.forEach(({ label, value, x, y }) => {
    doc
      .setFont("helvetica", "bold")
      .setFontSize(10)
      .text(startCase(label), x, y);
    doc.setFont("helvetica", "normal").text(":", x + 36, y);

    const lines = doc.splitTextToSize(value, 75);
    const lineHeight = 4.5;

    const valueX = x + 38;
    const valueY = y;

    lines.forEach((line: string, idx: number) => {
      doc.text(line, valueX, valueY + idx * lineHeight);
    });
  });
}

function drawHeader(
  doc: any,
  headerData: PrintDataEntitas,
  logo: string,
  t: Function
) {
  doc
    .setFont("helvetica", "bold")
    .setFontSize(12)
    .setTextColor("#4d4d4d");
  doc.text("COMMERCIAL INVOICE", 7, 12);
  doc.addImage(logo, "PNG", 135, 15, 60, 14, "logo", "FAST");

  const fields = [
    [
      t("invoice.date"),
      dateToDateString(headerData.sttCreatedAt, false, false, t("locale"))
    ],
    ["STT", headerData.sttNo]
  ];

  fields.forEach(([label, value], i) => {
    doc
      .setFont("helvetica", "bold")
      .setFontSize(10)
      .text(startCase(label), 7, 23 + i * 7);
    doc.setFont("helvetica", "normal").text(":", 40, 23 + i * 7);
    doc.text(value, 42, 23 + i * 7);
  });

  doc.setFillColor("#e0e0e0").rect(0, 40, 250, 0.2, "F");

  drawPartyInfo(doc, headerData, t);
}

function prepareData(headerData: PrintDataEntitas) {
  const country = headerData.sttBookedByCountry || undefined;
  const currency = `${formatByCountry(headerData.sttGoodsEstimatePrice, {
    type: "money",
    country
  })} ${headerData.currencyCode}`;

  return [
    {
      no: 1,
      itemDescription: ellipsisString(headerData.sttCommodityDescription, 30),
      unitValue: currency,
      qty: formatByCountry(headerData.sttPieces.length, {
        type: "quantity",
        country
      }),
      totalValue: currency,
      weight: formatByCountry(headerData.sttChargeableWeight, {
        type: "weight",
        country
      })
    }
  ];
}

function getColumnsTable(t: Function) {
  return [
    { header: "No.", dataKey: "no" },
    { header: t("invoice.itemDescription"), dataKey: "itemDescription" },
    {
      header: `${t("invoice.unitValue")}`,
      dataKey: "unitValue"
    },
    { header: t("invoice.qty"), dataKey: "qty" },
    { header: t("invoice.totalValue"), dataKey: "totalValue" },
    { header: `${t("invoice.weight")} (Kg)`, dataKey: "weight" }
  ];
}

function getColumnStyles() {
  return {
    0: { cellWidth: 13 },
    1: { cellWidth: 60 },
    2: { cellWidth: 40 },
    3: { cellWidth: 30 },
    4: { cellWidth: 30 },
    5: { cellWidth: 25 }
  };
}

function getTableStyles() {
  return {
    headStyles: {
      font: "helvetica",
      fontStyle: "bold",
      fillColor: "#f5f6f7",
      textColor: "#1a1421",
      fontSize: 8,
      halign: "left",
      valign: "top",
      cellPadding: { top: 4, right: 2, bottom: 4, left: 2 }
    },
    bodyStyles: {
      font: "helvetica",
      fontStyle: "normal",
      fillColor: "#fff",
      textColor: "#1a1421",
      fontSize: 10,
      halign: "left",
      valign: "top",
      lineColor: "#e0e0e0",
      lineWidth: { top: 0.1, right: 0, bottom: 0.1, left: 0 },
      cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
      cellWidth: "auto"
    }
  };
}

function handleCellDrawing(
  cellData: any,
  dataLength: number,
  updateLastPos: Function
) {
  if (cellData.row.index < 0) return;

  const centerColumns = ["unitValue", "qty", "totalValue", "weight"];
  const isHeadOrBody = ["head", "body"].includes(cellData.row.section);

  if (isHeadOrBody && centerColumns.includes(cellData.column.dataKey)) {
    cellData.row.cells[cellData.column.dataKey].styles = {
      ...cellData.row.cells[cellData.column.dataKey].styles,
      halign: "center"
    };
  }

  updateLastPos();

  if (
    cellData.row.index === dataLength - 2 &&
    cellData.cursor.y + cellData.row.height + 20 > 270
  ) {
    cellData.cell.contentHeight = 100;
    cellData.row.height = 100;
  }
}

type TableConfig = {
  data: any[];
  columnsTable: any[];
  columnStyles: any;
  tableStyles: any;
};

function drawTable(doc: any, config: TableConfig): void {
  let lastY = 0;
  let lastHeight = 0;

  const { data, columnsTable, columnStyles, tableStyles } = config;

  doc.autoTable({
    body: data,
    columns: columnsTable,
    theme: "plain",
    startY: doc.internal.getCurrentPageInfo().pageNumber === 1 ? 90 : 0,
    margin: { top: 7, left: 7, right: 7, bottom: 7 },
    ...tableStyles,
    columnStyles,
    willDrawCell: (cellData: any) => {
      handleCellDrawing(cellData, data.length, () => {
        lastY = cellData.cursor.y;
        lastHeight = cellData.row.height;
      });
    }
  });

  if (data.length >= 3 && data.length <= 5) {
    doc.addPage("a4", "l");
  }
}

function drawMainTable(doc: any, headerData: PrintDataEntitas, t: Function) {
  const data = prepareData(headerData);
  const columnsTable = getColumnsTable(t);
  const columnStyles = getColumnStyles();
  const tableStyles = getTableStyles();

  drawTable(doc, {
    data,
    columnsTable,
    columnStyles,
    tableStyles
  });
}

function drawTermsAndSignature(
  doc: any,
  headerData: PrintDataEntitas,
  t: Function
) {
  const columnsTerm = [{ dataKey: "term" }, { dataKey: "sign" }];
  const columnTermStyle = {
    0: { cellWidth: 130 },
    1: { cellWidth: 60 }
  };

  doc.autoTable({
    body: [
      { term: t("invoice.note") },
      { term: t("invoice.term") },
      {
        sign: `${startCase(t("invoice.signatureOfShipper"))}\n\n\n\n\n\n${
          headerData.sttSenderName
        }`
      }
    ],
    columns: columnsTerm,
    theme: "plain",
    startY: doc.previousAutoTable ? doc.previousAutoTable.finalY + 10 : 150,
    margin: { top: 7, left: 7, right: 7, bottom: 7 },
    bodyStyles: {
      font: "helvetica",
      fontStyle: "normal",
      fillColor: "#fff",
      textColor: "#1a1421",
      fontSize: 8,
      halign: "left",
      valign: "top",
      lineColor: "#e0e0e0",
      lineWidth: { top: 0, right: 0, bottom: 0, left: 0 },
      cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
      cellWidth: "auto"
    },
    columnStyles: columnTermStyle,
    willDrawCell: (data: any) => {
      if (data.row.index < 0) return;

      if (data.column.dataKey === "sign" || data.row.section === "body") {
        data.row.cells["sign"].styles = {
          ...data.row.cells["sign"].styles,
          fontStyle: "bold",
          halign: "center"
        };
      }
    }
  });
}

const generateCommercialInvoice = (
  headerData: PrintDataEntitas,
  logo: string,
  language = "en"
) => {
  const doc: any = new jsPDF("p", "mm", "a4", true);
  const translate = setupI18n(language);

  drawHeader(doc, headerData, logo, translate);
  drawMainTable(doc, headerData, translate);
  drawTermsAndSignature(doc, headerData, translate);

  doc.setProperties({ title: "Commercial Invoice" });
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

export default generateCommercialInvoice;
